import { Layers, Sparkles, Lock } from 'lucide-react';
import { cn } from '../lib/cn';
import { useDesignSystem } from './DesignSystemProvider';
import type { DesignSystemMode } from './design-system-mode';

const OPTIONS: Array<{ value: DesignSystemMode; label: string; icon: typeof Layers; hint: string }> = [
  { value: 'legacy', label: 'Legacy', icon: Layers, hint: 'Current app UI' },
  { value: 'modern', label: 'Modern', icon: Sparkles, hint: 'Experimental' },
];

/**
 * DesignSystemToggle — the "Design System Mode" switch. Drop it into the
 * existing Settings page. Styled with neutral tokens so it looks at home in
 * the legacy UI too. When VITE_ENABLE_MODERN_UI forces modern, the control
 * shows a locked state.
 *
 * Must be rendered inside a DesignSystemProvider.
 */
export function DesignSystemToggle({ className }: { className?: string }) {
  const { mode, setMode, envForced } = useDesignSystem();

  return (
    <div className={cn('inline-flex flex-col gap-1.5', className)}>
      <div
        role="radiogroup"
        aria-label="Design system mode"
        className={cn(
          'inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 p-1',
          envForced && 'opacity-70',
        )}
      >
        {OPTIONS.map((option) => {
          const Icon = option.icon;
          const active = mode === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={envForced}
              onClick={() => setMode(option.value)}
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-medium transition-colors',
                active
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
                envForced && 'cursor-not-allowed',
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
              {option.label}
            </button>
          );
        })}
      </div>
      {envForced && (
        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <Lock className="h-3 w-3" aria-hidden />
          Locked on by VITE_ENABLE_MODERN_UI
        </span>
      )}
    </div>
  );
}
